<template>
    <div>
        <Molded class="record-search-modal-context" :padding-left="15" :padding-right="15">
            <span class="record-search-modal-label">Vincular ao item do extrato</span>
            <div class="record-search-modal-description">{{ statementItem.description }}</div>
            <div class="record-search-modal-context-row">
                <div class="record-search-modal-description">{{ statementItem.dueDate }}</div>
                <div class="record-search-modal-value"
                    :class="{ 'record-search-modal-value-negative': statementItem.value < 0, 'record-search-modal-value-positive': statementItem.value >= 0 }">
                    {{ statementItem.value | currency }}
                </div>
            </div>
        </Molded>

        <b-row>

            <b-col sm="9">
                <InputText title="Buscar por descrição, destinatário ou valor" field="search"
                    formName="recordSearchModalForm" v-model="search" />
            </b-col>
            <b-col sm="3">
                <label class="record-search-modal-filter-spacer">&nbsp;</label>
                <Button _key="btnOpenRecordFilter" title="Filtro" size="small" classIcon="fa-solid fa-filter"
                    :type="hasActiveFilter ? 'primary' : 'info'" :clicked="onOpenFilter" />
            </b-col>
        </b-row>

        <div class="record-search-modal-list">
            <Loading v-if="loading && items.length == 0" type="line" :center="false" />
            <div v-else-if="items.length == 0" class="record-search-modal-empty">
                Nenhum lançamento encontrado.
            </div>
            <Molded v-for="item in items" :key="item.id" class="record-search-modal-item">
                <div class="record-search-modal-item-content">
                    <div>
                        <div class="record-search-modal-item-description">
                            {{ item.recipientName || item.description }}
                        </div>
                        <div class="record-search-modal-subtext" v-if="item.recipientName">
                            {{ item.description }}
                        </div>
                        <div class="record-search-modal-subtext">{{ item.dueDate }}</div>
                        <div class="record-search-modal-subtext" v-if="item.status == 'parcial'">
                            Saldo:
                            <span :class="item.value < 0 ? 'record-search-modal-value-negative' : 'record-search-modal-value-positive'">
                                {{ (item.value < 0 ? -item.remainingValue : item.remainingValue) | currency }}
                            </span>
                            de {{ item.value | currency }}
                        </div>
                        <div class="record-search-modal-item-warning" v-if="!fitsBalance(item)">
                            Saldo insuficiente para este valor
                        </div>
                    </div>
                    <div class="record-search-modal-item-side">
                        <div class="record-search-modal-item-value"
                            :class="{ 'record-search-modal-value-negative': item.value < 0, 'record-search-modal-value-positive': item.value >= 0 }">
                            {{ item.value | currency }}
                        </div>
                        <Button :_key="'btnLink' + item.id" title="Vincular" size="small" type="primary"
                            :disabled="!fitsBalance(item)" :clicked="() => onSelect(item)" />
                    </div>
                </div>
            </Molded>
            <div class="record-search-modal-load-more text-center" v-if="items.length > 0 && items.length < totalRecords">
                <Button _key="btnLoadMoreRecordSearch" title="Carregar mais" size="small" type="info"
                    :clicked="loadMore" />
            </div>
        </div>

        <div class="record-search-modal-actions text-center">
            <Button _key="btnCancelRecordSearch" title="Cancelar" size="medium" type="danger" :clicked="onCancel" />
        </div>
    </div>
</template>
<script>

import Molded from "@/ui/layout/Molded";
import Button from "@/ui/forms/Button";
import InputText from "@/ui/forms/InputText";
import Loading from "@/ui/shared/Loading.vue";

import BankReconciliationService from "../../services/bankReconciliation.service.js";
import { withinBalance } from "../../mixins/reconciliationRecord.mixin.js";

import { mapActions } from "vuex";

export default {
    name: "RecordSearchModal",
    components: { Molded, Button, InputText, Loading },
    props: {
        statementItem: Object,
        filter: {
            type: Object,
            default: () => ({ period: [], valueStart: 0, valueEnd: 0 }),
        },
    },
    data() {
        return {
            search: "",
            items: [],
            loading: false,
            currentPage: 1,
            totalPerPage: 20,
            totalRecords: 0,
            searchTimeout: null,
        };
    },
    created() {
        this.searchRecords(true);
    },
    beforeDestroy() {
        clearTimeout(this.searchTimeout);
    },
    computed: {
        hasActiveFilter() {
            let period = this.filter.period || [];
            return (period.length == 2 && period[0] && period[1]) || this.filter.valueStart > 0 || this.filter.valueEnd > 0;
        },
    },
    methods: {
        ...mapActions("generic", ["getApi"]),
        fitsBalance(item) {
            return withinBalance(Math.abs(this.statementItem.value), item.remainingValue);
        },
        parseBrDateToIso(str) {
            if (!str) return undefined;
            let parts = str.split('/');
            if (parts.length != 3) return undefined;
            return `${parts[2]}-${parts[1]}-${parts[0]}`;
        },
        async searchRecords(reset) {
            if (reset) {
                this.currentPage = 1;
                this.items = [];
            }

            let period = this.filter.period || [];

            let params = {
                Search: this.search || undefined,
                Type: this.statementItem.type,
                DateStart: this.parseBrDateToIso(period[0]),
                DateEnd: this.parseBrDateToIso(period[1]),
                ValueStart: this.filter.valueStart > 0 ? this.filter.valueStart : undefined,
                ValueEnd: this.filter.valueEnd > 0 ? this.filter.valueEnd : undefined,
                CurrentPage: this.currentPage,
                TotalPerPage: this.totalPerPage,
            };

            this.loading = true;
            let response = await this.getApi({ url: BankReconciliationService.urlGetAllRecord, obj: params });
            let content = (response && response.content) || {};

            let items = (content.items || []).map((item) => ({
                ...item,
                remainingValue: Math.abs((item.totalValue || 0) - (item.paidValue || 0)),
            }));

            if (reset) this.items = items;
            else this.items = this.items.concat(items);

            this.totalRecords = content.totalRecords || 0;
            this.loading = false;
        },
        loadMore() {
            this.currentPage++;
            this.searchRecords(false);
        },
        onOpenFilter() {
            this.$emit('open-filter');
        },
        onSelect(item) {
            this.$emit('select', item);
        },
        onCancel() {
            this.$emit('cancel');
        },
    },
    watch: {
        search() {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => {
                this.searchRecords(true);
            }, 300);
        },
        filter: {
            handler() {
                this.searchRecords(true);
            },
            deep: true,
        },
    },
};
</script>
<style scoped>
.record-search-modal-context {
    margin-bottom: 10px;
}

.record-search-modal-filter-spacer {
    display: block;
    visibility: hidden;
    margin-bottom: 8px;
}

.record-search-modal-label {
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.record-search-modal-description {
    font-weight: 600;
    margin-top: 4px;
}

.record-search-modal-subtext {
    color: var(--text-secondary);
    margin-top: 2px;
}

.record-search-modal-item-warning {
    font-size: 11.5px;
    color: var(--color-danger);
    margin-top: 2px;
}

.record-search-modal-context-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 8px;
}

.record-search-modal-value {
    color: var(--text-primary);
}

.record-search-modal-value-positive {
    color: var(--color-success);
}

.record-search-modal-value-negative {
    color: var(--color-danger);
}

.record-search-modal-list {
    max-height: 220px;
    overflow-y: auto;
    margin-top: 10px;
}

.record-search-modal-item {
    margin-bottom: 10px;
}

.record-search-modal-item-content {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
}

.record-search-modal-item-description {
    font-weight: 600;
    color: var(--text-primary);
}

.record-search-modal-item-side {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 6px;
    flex-shrink: 0;
}

.record-search-modal-empty {
    padding: 24px 16px;
    text-align: center;
    color: var(--text-muted);
}

.record-search-modal-load-more {
    margin-top: 8px;
}

.record-search-modal-actions {
    margin-top: 12px;
}
</style>
